import React, { useMemo } from 'react'

import { CloudinaryImage } from 'ethos-design-system'

import { useIntersectionObserver } from '../../../../lib/@getethos/hooks/useIntersectionObserver'
import {
  EstimateWidgetImageLayout,
  EstimateWidgetModuleData,
} from '../EstimateWidget'
import { EstimateWidgetProvider } from '../contexts/EstimateWidgetContext'
import styles from './Floating.module.scss'
import { FloatingFormManagerRefactor } from './FloatingForm/FloatingFormManagerRefactor'
import {
  CoBranded,
  External,
  ForegroundImage,
  FullbleedImage,
  MultiImage,
  StraightToApp,
} from './Layouts'
import { FinalExpense } from './Layouts/FinalExpense/FInalExpense'

export const FloatingWidget = ({
  moduleData,
}: {
  moduleData: EstimateWidgetModuleData
}) => {
  const [scrollNode, updateScrollNode] = useIntersectionObserver()

  const formManager = useMemo(
    () => (
      <FloatingFormManagerRefactor
        moduleData={moduleData}
        scrollNode={scrollNode as unknown as Element}
      />
    ),
    [moduleData, scrollNode]
  )

  // showPlaceholder can be used for experiments that modify the EW layout
  // to do this turn showPlaceholder into a useState constant and modify
  // it's value with useLayoutEffect once the Optimizely experiment flag
  // is ready to set the EW layout to it's final variation
  const showPlaceholder = false

  if (showPlaceholder)
    return (
      <div className={styles.placeholder}>
        {moduleData.groupImage && moduleData.imageAlt && (
          <CloudinaryImage
            publicId={moduleData.groupImage}
            alt={moduleData.imageAlt}
            className={styles.image}
            crop={CloudinaryImage.CROP_METHODS.CROP}
          />
        )}
      </div>
    )

  let moduleToRender: React.ReactNode

  if (moduleData.imageLayout === EstimateWidgetImageLayout.MultiImage) {
    // MultiImage variation

    moduleToRender = (
      <MultiImage
        formManager={formManager}
        moduleData={moduleData}
        updateScrollNode={updateScrollNode}
      />
    )
  } else if (
    moduleData.imageLayout === EstimateWidgetImageLayout.FinalExpense
  ) {
    moduleToRender = (
      <FinalExpense formManager={formManager} moduleData={moduleData} />
    )
  } else if (moduleData.imageLayout === EstimateWidgetImageLayout.CoBranded) {
    moduleToRender = (
      <CoBranded
        formManager={formManager}
        moduleData={moduleData}
        updateScrollNode={updateScrollNode}
      />
    )
  } else if (moduleData.imageLayout === EstimateWidgetImageLayout.FullBleed) {
    moduleToRender = (
      <FullbleedImage
        formManager={formManager}
        moduleData={moduleData}
        updateScrollNode={updateScrollNode}
      />
    )
  } else if (moduleData.imageLayout === EstimateWidgetImageLayout.External) {
    moduleToRender = (
      <External
        formManager={formManager}
        moduleData={moduleData}
        updateScrollNode={updateScrollNode}
      />
    )
  } else if (
    moduleData.imageLayout === EstimateWidgetImageLayout.ForegroundImage
  ) {
    moduleToRender = (
      <ForegroundImage
        formManager={formManager}
        moduleData={moduleData}
        updateScrollNode={updateScrollNode}
      />
    )
  } else if (
    moduleData.imageLayout === EstimateWidgetImageLayout.StraightToApp
  ) {
    moduleToRender = (
      <StraightToApp
        formManager={formManager}
        moduleData={moduleData}
        updateScrollNode={updateScrollNode}
      />
    )
  } else
    moduleToRender = (
      <FullbleedImage
        formManager={formManager}
        moduleData={moduleData}
        updateScrollNode={updateScrollNode}
      />
    )

  return <EstimateWidgetProvider>{moduleToRender}</EstimateWidgetProvider>
}
